import { Box } from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'
import { FormatarNumero } from '../../../../../Utils/Helper'
import { iIndicador, iMesPeriodo } from '../../../gestao'
import { TableDiv } from './styles'

interface iTabelaIndicadorMensalProps {
  indicador: iIndicador
  meses: iMesPeriodo[]
}

const mesesAbreviados = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
]

function formatar(ind: iIndicador, valor?: number | null): string {
  if (valor === null || valor === undefined) return '-'
  if (ind.tipoMeta === 0) {
    // Oculta os centavos para indicadores monetários (tipoMeta 0)
    const semCentavos = FormatarNumero(valor, ind).split(',')[0]
    return `${ind.medidaMeta ?? ''}${semCentavos}`
  }
  return `${FormatarNumero(valor, ind)}${ind.medidaMeta ?? ''}`
}

interface iColuna {
  real: string
  previsto: string
  acumulado: string
}

export const TabelaIndicadorMensal: React.FC<iTabelaIndicadorMensalProps> = ({ indicador, meses }) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const [fontSize, setFontSize] = useState('.7rem')

  const colunas: iColuna[] = []
  let acumulado = 0
  let maxLen = 1

  for (let i = 1; i <= 12; i++) {
    const mes = meses.find(e => e.numeroMes === i)
    let acumuladoMes: number | null = null

    if (mes) {
      if (indicador.tipoMeta === 5) {
        acumuladoMes = mes.real ?? 0
      } else {
        acumulado += mes.real ?? 0
        acumuladoMes = acumulado
      }
    }

    const coluna: iColuna = {
      real: formatar(indicador, mes?.real ?? null),
      previsto: formatar(indicador, mes?.previsto ?? null),
      acumulado: mes ? formatar(indicador, acumuladoMes) : '-'
    }
    maxLen = Math.max(maxLen, coluna.real.length, coluna.previsto.length, coluna.acumulado.length)
    colunas.push(coluna)
  }

  useEffect(() => {
    const el = wrapperRef.current
    if (!el) return

    function recalcular(): void {
      const largura = el?.clientWidth ?? 0
      if (largura <= 0) return
      const larguraPrimeira = 80 // 5rem da primeira coluna
      const larguraColuna = (largura - larguraPrimeira) / 12
      const padding = 12 // padding horizontal aproximado por célula
      const disponivel = Math.max(0, larguraColuna - padding)
      const fator = 0.62 // largura média de caractere em relação ao tamanho da fonte
      let fontPx = disponivel / (maxLen * fator)
      fontPx = Math.max(8, Math.min(13, fontPx))
      setFontSize(`${fontPx.toFixed(1)}px`)
    }

    recalcular()
    const observer = new ResizeObserver(recalcular)
    observer.observe(el)
    return () => observer.disconnect()
  }, [maxLen])

  return (
    <Box ref={wrapperRef} w={'100%'} mt={'.75rem'}>
      <TableDiv style={{ fontSize }}>
        <table>
          <thead>
            <tr>
              <th>Valores</th>
              {mesesAbreviados.map(e => (
                <th key={e}>{e}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Real</td>
              {colunas.map((c, i) => (
                <td key={`real-${i}`}>{c.real}</td>
              ))}
            </tr>
            <tr>
              <td>Previsto</td>
              {colunas.map((c, i) => (
                <td key={`previsto-${i}`}>{c.previsto}</td>
              ))}
            </tr>
            <tr>
              <td>Acumulado</td>
              {colunas.map((c, i) => (
                <td key={`acumulado-${i}`}>{c.acumulado}</td>
              ))}
            </tr>
          </tbody>
        </table>
      </TableDiv>
    </Box>
  )
}
